import React from 'react';
import { Text, View } from '@react-pdf/renderer';
import { formatNumber } from 'utils';

interface SummaryTableProps {
  invoiceData: {
    totalBeforeTax?: number;
    totalTax?: number;
    totalAmount?: number;
    totalAmountInWords?: string;
    totalsByTaxRate?: Record<
      number,
      {
        totalBeforeTax: number;
        totalTax: number;
        totalAmount: number;
      }
    >;
  };
}

const styles = {
  table: {
    marginTop: 2,
    border: '1px solid black',
    maxHeight: 400
  },
  tableHeader: {
    flexDirection: 'row' as const,
    borderBottom: '1px solid black',
    fontSize: 9,
    minHeight: 25
  },
  tableRow: {
    flexDirection: 'row' as const,
    borderBottom: '1px dashed #ccc',
    minHeight: 20,
    alignItems: 'center' as const
  },
  tableCell: {
    padding: 4,
    fontSize: 9,
    textAlign: 'center' as const,
    borderRight: '1px solid black'
  },
  tableCellLeft: {
    padding: 4,
    fontSize: 9,
    textAlign: 'left' as const,
    borderRight: '1px solid black',
    display: 'flex' as const,
    flexDirection: 'column' as const,
    justifyContent: 'center' as const
  },
  tableHeaderCell: {
    padding: 4,
    fontSize: 9,
    textAlign: 'center' as const,
    borderRight: '1px solid black',
    display: 'flex' as const,
    flexDirection: 'column' as const,
    justifyContent: 'center' as const,
    alignItems: 'center' as const
  },
  tableHeaderCellLast: {
    padding: 4,
    fontSize: 9,
    textAlign: 'center' as const,
    display: 'flex' as const,
    flexDirection: 'column' as const,
    justifyContent: 'center' as const,
    alignItems: 'center' as const
  }
};

const SummaryTable: React.FC<SummaryTableProps> = ({ invoiceData }) => (
  <View style={styles.table}>
    <View style={styles.tableHeader}>
      <View style={[styles.tableHeaderCell, { width: '25%' }]}>
        <Text style={{ fontWeight: 'bold' }}>Tổng hợp</Text>
        <Text style={{ fontSize: 7 }}>(Summary)</Text>
      </View>
      <View style={[styles.tableHeaderCell, { width: '15%' }]}>
        <Text style={{ fontWeight: 'bold' }}>Thuế suất</Text>
        <Text style={{ fontSize: 7 }}>(VAT rate)</Text>
      </View>
      <View style={[styles.tableHeaderCell, { width: '20%' }]}>
        <Text style={{ fontWeight: 'bold' }}>Tổng tiền chưa thuế</Text>
        <Text style={{ fontSize: 7 }}>(Amount before VAT)</Text>
      </View>
      <View style={[styles.tableHeaderCell, { width: '20%' }]}>
        <Text style={{ fontWeight: 'bold' }}>Tổng tiền thuế</Text>
        <Text style={{ fontSize: 7 }}>(VAT amount)</Text>
      </View>
      <View style={[styles.tableHeaderCellLast, { width: '20%' }]}>
        <Text style={{ fontWeight: 'bold' }}>Giá trị thanh toán</Text>
        <Text style={{ fontSize: 7 }}>(Total amount)</Text>
      </View>
    </View>
    {invoiceData.totalsByTaxRate ? (
      Object.entries(invoiceData.totalsByTaxRate).map(([taxRate, totals]) => {
        const totalsData = totals as {
          totalBeforeTax: number;
          totalTax: number;
          totalAmount: number;
        };
        return (
          <View key={taxRate} style={styles.tableRow}>
            <View style={[styles.tableCellLeft, { width: '25%' }]}>
              <Text>Tổng tiền chịu thuế suất</Text>
              <Text style={{ fontSize: 7 }}>(Total amount)</Text>
            </View>
            <Text style={[styles.tableCell, { width: '15%' }]}>{taxRate}%</Text>
            <Text style={[styles.tableCell, { width: '20%' }]}>
              {formatNumber(totalsData.totalBeforeTax)}
            </Text>
            <Text style={[styles.tableCell, { width: '20%' }]}>
              {formatNumber(totalsData.totalTax)}
            </Text>
            <Text style={[styles.tableHeaderCellLast, { width: '20%' }]}>
              {formatNumber(totalsData.totalAmount)}
            </Text>
          </View>
        );
      })
    ) : (
      <View style={styles.tableRow}>
        <View style={[styles.tableCellLeft, { width: '25%' }]}>
          <Text>Tổng tiền chịu thuế suất</Text>
          <Text style={{ fontSize: 7 }}>(Total amount)</Text>
        </View>
        <Text style={[styles.tableCell, { width: '15%' }]}>8%</Text>
        <Text style={[styles.tableCell, { width: '20%' }]}>
          {invoiceData.totalBeforeTax ? formatNumber(invoiceData.totalBeforeTax) : ''}
        </Text>
        <Text style={[styles.tableCell, { width: '20%' }]}>
          {invoiceData.totalTax ? formatNumber(invoiceData.totalTax) : ''}
        </Text>
        <Text style={[styles.tableHeaderCellLast, { width: '20%' }]}>
          {invoiceData.totalAmount ? formatNumber(invoiceData.totalAmount) : ''}
        </Text>
      </View>
    )}
    <View style={styles.tableRow}>
      <View style={[styles.tableCellLeft, { width: '25%' }]}>
        <Text style={{ fontWeight: 'bold' }}>Tổng cộng tiền thanh toán</Text>
        <Text style={{ fontSize: 7 }}>(Total payment)</Text>
      </View>
      <Text style={[styles.tableCell, { width: '15%' }]}></Text>
      <Text style={[styles.tableCell, { width: '20%' }]}>
        {invoiceData.totalBeforeTax ? formatNumber(invoiceData.totalBeforeTax) : ''}
      </Text>
      <Text style={[styles.tableCell, { width: '20%' }]}>
        {invoiceData.totalTax ? formatNumber(invoiceData.totalTax) : ''}
      </Text>
      <Text style={[styles.tableHeaderCellLast, { width: '20%' }]}>
        {invoiceData.totalAmount ? formatNumber(invoiceData.totalAmount) : ''}
      </Text>
    </View>
    <View style={styles.tableRow}>
      <Text
        style={[styles.tableCellLeft, { width: '100%', textAlign: 'left', fontWeight: 'bold' }]}
      >
        Tổng số tiền viết bằng chữ (Total amount in words): {invoiceData.totalAmountInWords}
      </Text>
    </View>
  </View>
);

export default SummaryTable;
